import { useState } from "react";
import { useMutation, useQuery } from "react-query";
import { Link, useNavigate, useParams } from "react-router-dom";
import { Alert, Button, Card, Col, Form, Row, Spinner } from "react-bootstrap";
import { useForm } from "react-hook-form";
import { AxiosError } from "axios";
import Icon from "@mdi/react";
import { mdiClipboardPulseOutline, mdiChevronLeft, mdiPulse } from "@mdi/js";

import { useToast } from "../../context/ToastContext";
import { getGruposGrupo, getGetGruposGrupoQueryKey, postGruposCadastrar, postGruposAtualizar } from "../../config/swagger";
import type { CadastrarGrupoRequest } from "../../model/cadastrarGrupoRequest";
import type { AtualizarGrupoRequest } from "../../model/atualizarGrupoRequest";
import type { CadastrarGrupoResponseIResponse } from "../../model/cadastrarGrupoResponseIResponse";
import type { AtualizarGrupoResponseIResponse } from "../../model/atualizarGrupoResponseIResponse";
import type { ExibirGrupoResponse } from "../../model/exibirGrupoResponse";
import { getSaudeIcon, getSaudeDescricao } from "../../utils/saude";

import Layout from "../../components/Layout";
import MonitoresCard from "../../components/MonitoresCard";

const toastTitle = "Grupo";

export default function GrupoFormulario() {
	const navigate = useNavigate();
	const { id } = useParams();
	const { handleToast } = useToast();

	const [formStatus, setFormStatus] = useState(id ? false : true);
	const [formSaving, setFormSaving] = useState(false);

	const { data, isLoading, isFetching, isRefetching } = useQuery(getGetGruposGrupoQueryKey({ id: Number(id) }), () => fetchData(id!), { enabled: !!id && !formStatus });

	const { register, handleSubmit, reset } = useForm<ExibirGrupoResponse>({ defaultValues: data?.Data });

	const mutationPost = useMutation(mutatePost, { onSuccess: mutatePostSuccess, onError: mutateError });
	const mutationPut = useMutation(mutatePut, { onSuccess: mutatePutSuccess, onError: mutateError });

	async function fetchData(id: string) {
		const resp = await getGruposGrupo({ id: Number(id) });
		reset(resp.Data);
		return resp;
	}

	async function mutatePost(data: CadastrarGrupoRequest) {
		setFormSaving(true);
		return await postGruposCadastrar(data);
	}

	function mutatePostSuccess(resp: CadastrarGrupoResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		if (resp.Data?.id) {
			navigate("/grupoFormulario/" + resp.Data.id);
		}
		setFormStatus(false);
		setFormSaving(false);
	}

	async function mutatePut(data: AtualizarGrupoRequest) {
		setFormSaving(true);
		return await postGruposAtualizar(data);
	}

	function mutatePutSuccess(resp: AtualizarGrupoResponseIResponse) {
		handleToast(toastTitle, "Informações salvas com sucesso!", 5000);
		setFormStatus(false);
		setFormSaving(false);
	}

	function mutateError(resp: AxiosError) {
		handleToast(toastTitle, `Problema na operação: ${resp.message}`, 5000, "danger");
		setFormSaving(false);
	}

	const iconClass = getSaudeIcon(data?.Data?.saude);
	const saudeDescricao = getSaudeDescricao(data?.Data?.saude);

	return (
		<Layout>
			<Form
				onSubmit={handleSubmit((values) => {
					if (id) {
						mutationPut.mutate({
							id: Number(id),
							nome: values.nome,
							descricao: values.descricao,
						});
					} else {
						mutationPost.mutate({
							nome: values.nome,
							descricao: values.descricao,
						});
					}
				})}
			>
				<h5 className="mt-4 mb-4 d-flex align-items-center fw-light">
					<Link to={"/grupos"} className="d-flex align-items-center text-decoration-none">
						<Icon path={mdiChevronLeft} size={1} className="me-1" /> <Icon path={mdiClipboardPulseOutline} size={1} className="me-2" /> Formulário de Grupo
					</Link>
					{(isLoading || isFetching || isRefetching) && <Spinner size="sm" className="ms-1" variant="secondary" />}
					<div className="d-flex gap-2 float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
						{!formStatus && (
							<Button
								variant="dark"
								className="rounded-3 shadow-sm"
								onClick={() => {
									setFormStatus(true);
								}}
							>
								Editar Informações
							</Button>
						)}
						{formStatus && (
							<>
								<Button
									variant="light"
									className="rounded-3 shadow-sm"
									type="button"
									onClick={() => {
										if (id) {
											setFormStatus(false);
										} else {
											navigate("/grupos");
										}
									}}
									disabled={formSaving}
								>
									Cancelar
								</Button>
								<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
									{formSaving ? (
										<>
											<Spinner animation="border" size="sm" className="me-2" /> Salvando
										</>
									) : (
										"Salvar Informações"
									)}
								</Button>
							</>
						)}
					</div>
				</h5>

				<Card className="mb-3">
					<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Informações Básicas</Card.Header>
					<Card.Body>
						<Form.Group className="mb-3" controlId="nome">
							<Form.Label>Nome</Form.Label>
							<Form.Control type="text" placeholder="Informe aqui" {...register("nome", { required: true })} disabled={!formStatus} />
						</Form.Group>
						<Form.Group className="mb-3" controlId="descricao">
							<Form.Label>Descrição</Form.Label>
							<Form.Control as="textarea" rows={3} placeholder="Informe aqui" {...register("descricao")} disabled={!formStatus} />
						</Form.Group>
					</Card.Body>
				</Card>

				<div className="d-flex gap-2 mb-4">
					{!formStatus && (
						<Button
							variant="dark"
							className="rounded-3 shadow-sm"
							onClick={() => {
								setFormStatus(true);
							}}
						>
							Editar Informações
						</Button>
					)}
					{formStatus && (
						<>
							<Button variant="primary" className="rounded-3 shadow-sm" type="submit" disabled={formSaving}>
								{formSaving ? (
									<>
										<Spinner animation="border" size="sm" className="me-2" /> Salvando
									</>
								) : (
									"Salvar Informações"
								)}
							</Button>
							<Button
								variant="light"
								className="rounded-3 shadow-sm"
								type="button"
								onClick={() => {
									if (id) {
										setFormStatus(false);
									} else {
										navigate("/grupos");
									}
								}}
								disabled={formSaving}
							>
								Cancelar
							</Button>
						</>
					)}
				</div>

				{!formStatus && !isLoading && (
					<>
						<Card className="mb-4">
							<Card.Header className="d-flex align-items-center justify-content-between bg-white fs-5 fw-light p-3">Status</Card.Header>
							<Card.Body>
								<Row>
									<Col md={4}>
										<div className="fw-lighter">Saúde</div>
										<div className="fw-light">
											<Icon path={mdiPulse} size={1} className={`me-1 ${iconClass}`} /> {saudeDescricao}
										</div>
									</Col>
									<Col md={4}>
										<div className="fw-lighter">Latência</div>
										<div className="fw-light">{data?.Data?.ultimaLatencia ?? "..."}</div>
									</Col>
									<Col md={4}>
										<div className="fw-lighter">Data Hora Checagem</div>
										<div className="fw-light">{data?.Data?.dataUltimaChecagem ?? "..."}</div>
									</Col>
								</Row>
							</Card.Body>
						</Card>

						<h5 className="mt-4 mb-2 d-flex align-items-center fw-light py-2">
							<Icon path={mdiPulse} size={1} className="me-2" /> Monitores Associado
							<div className="float-right ms-auto" style={{ marginTop: -10, marginBottom: -10 }}>
								<Button
									variant="primary"
									className="rounded-3 shadow-sm"
									size="sm"
									onClick={() => {
										navigate("/monitorFormulario?grupoId=" + data?.Data?.id);
									}}
								>
									Cadastrar
								</Button>
							</div>
						</h5>

						{data?.Data?.monitores && data?.Data?.monitores.length > 0 ? (
							<Row>
								{data.Data.monitores.map((monitor) => (
									<Col md={12} key={monitor.id}>
										<MonitoresCard data={monitor} />
									</Col>
								))}
							</Row>
						) : (
							<Alert variant="light" className="text-center">
								Nenhum monitor associado a este grupo
							</Alert>
						)}
					</>
				)}
			</Form>
		</Layout>
	);
}
